import React, { useState, useEffect } from 'react'
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Tooltip
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import TableLoading from 'components/TableLoading'
import {
  createNotificationSettings,
  deleteNotificationSetting,
  getNotificationSettings,
  NotificationSettings,
  updateNotificationSetting,
  NotificationType,
  resendVerification,
  refreshVerificationStatus,
  setPrimarySender
} from 'api/emailConfigs'

interface EmailFormData {
  email: string
  type: NotificationType
}

const NotificationSettingsPage = () => {
  const [notificationSettings, setNotificationSettings] = useState<
    NotificationSettings[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [editingEmail, setEditingEmail] = useState<NotificationSettings | null>(
    null
  )
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deletingEmail, setDeletingEmail] =
    useState<NotificationSettings | null>(null)
  const [busyRowId, setBusyRowId] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<EmailFormData>({
    defaultValues: {
      email: '',
      type: 'ORDER_NOTIFICATION'
    }
  })

  const fetchNotificationSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getNotificationSettings()
      setNotificationSettings(data)
    } catch (err: any) {
      setError(err || 'Failed to fetch notification settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotificationSettings()
  }, [])

  const getEmailCountByType = (type: NotificationType): number => {
    return notificationSettings.filter(setting => setting.type === type).length
  }

  const canDeleteEmail = (email: NotificationSettings): boolean => {
    return getEmailCountByType(email.type) > 1
  }

  const handleOpenDialog = (email?: NotificationSettings) => {
    if (email) {
      setEditingEmail(email)
      reset({ email: email.email, type: email.type })
    } else {
      setEditingEmail(null)
      reset({ email: '', type: 'ORDER_NOTIFICATION' })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingEmail(null)
    setDialogError(null)
    reset()
  }

  const [dialogError, setDialogError] = useState<string | null>(null)

  const showSuccess = (message: string) => {
    setSuccess(message)
    setTimeout(() => setSuccess(null), 4000)
  }

  const onSubmit = async (data: EmailFormData) => {
    try {
      setDialogError(null)
      if (editingEmail) {
        // Email itself is locked on edit (changing the address means a new
        // SES identity anyway — delete + add is cleaner). Only type moves.
        await updateNotificationSetting(editingEmail.id, { type: data.type })
        showSuccess('Notification type updated successfully')
      } else {
        const result = await createNotificationSettings(data)
        showSuccess(
          result.verificationEmailSent
            ? 'Email added. A verification link was sent by AWS SES — please confirm it before this address can send/receive notifications.'
            : 'Notification email added (already verified in SES)'
        )
      }
      handleCloseDialog()
      fetchNotificationSettings()
    } catch (err: any) {
      setDialogError(err || 'Operation failed')
    }
  }

  const handleResend = async (email: NotificationSettings) => {
    try {
      setBusyRowId(email.id)
      setError(null)
      const result = await resendVerification(email.id)
      showSuccess(
        result.verificationEmailSent
          ? `Verification email resent to ${email.email}`
          : `${email.email} is already verified`
      )
      fetchNotificationSettings()
    } catch (err: any) {
      setError(err || 'Failed to resend verification email')
    } finally {
      setBusyRowId(null)
    }
  }

  const handleRefreshStatus = async (email: NotificationSettings) => {
    try {
      setBusyRowId(email.id)
      setError(null)
      const updated = await refreshVerificationStatus(email.id)
      showSuccess(
        updated.verified
          ? `${email.email} is verified`
          : `${email.email} is still pending verification`
      )
      fetchNotificationSettings()
    } catch (err: any) {
      setError(err || 'Failed to refresh verification status')
    } finally {
      setBusyRowId(null)
    }
  }

  const handleSetSender = async (email: NotificationSettings) => {
    try {
      setBusyRowId(email.id)
      setError(null)
      await setPrimarySender(email.id)
      // In-place update so the row order does not shift. Refetching would
      // re-sort (and could repaint the chip/button cells out of order).
      setNotificationSettings(prev =>
        prev.map(item =>
          item.type === email.type
            ? { ...item, isPrimary: item.id === email.id }
            : item
        )
      )
      showSuccess(
        `${email.email} is now the sender for ${
          email.type === 'ORDER_NOTIFICATION'
            ? 'Order Notifications'
            : 'Customer Notifications'
        }`
      )
    } catch (err: any) {
      setError(err || 'Failed to set sender')
    } finally {
      setBusyRowId(null)
    }
  }

  const handleOpenDeleteDialog = (email: NotificationSettings) => {
    setDeletingEmail(email)
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
    setDeletingEmail(null)
  }

  const handleConfirmDelete = async () => {
    if (!deletingEmail) return

    try {
      setError(null)
      await deleteNotificationSetting(deletingEmail.id)
      showSuccess('Notification email deleted successfully')
      handleCloseDeleteDialog()
      fetchNotificationSettings()
    } catch (err: any) {
      setError(err || 'Failed to delete email')
      handleCloseDeleteDialog()
    }
  }

  return (
    <Container sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 3 }}
        >
          <Stack direction='row' alignItems='center' spacing={2}>
            <NotificationsIcon fontSize='large' color='primary' />
            <Typography variant='h4'>Notification Settings</Typography>
          </Stack>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Notification Email
          </Button>
        </Stack>

        <Alert severity='info' sx={{ mb: 3 }}>
          Manage email addresses for receiving notification emails. These emails
          will be used to send and receive system notifications and alerts.
          <strong>
            {' '}
            Each notification type must have at least one email. New addresses
            must be verified in AWS SES (we will email a confirmation link)
            before they can be used. The address marked as <em>Sender</em> is
            what customers see in the From field — only one Sender per type.
          </strong>
        </Alert>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity='success'
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {loading ? (
          <TableLoading />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email Address</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>SES Status</TableCell>
                  <TableCell>
                    <Tooltip title='The From address customers see. Only one Sender per type.'>
                      <span>Sender</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationSettings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center' sx={{ py: 4 }}>
                      No notification email addresses configured
                    </TableCell>
                  </TableRow>
                ) : (
                  notificationSettings.map(email => (
                    <TableRow key={email.id} hover>
                      <TableCell>
                        <Typography variant='body2' fontWeight='medium'>
                          {email.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            email.type === 'ORDER_NOTIFICATION'
                              ? 'Order Notification'
                              : 'Customer Notification'
                          }
                          size='small'
                          sx={{
                            bgcolor:
                              email.type === 'ORDER_NOTIFICATION'
                                ? '#1976d2'
                                : '#9c27b0',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {email.verified ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label='Verified'
                            size='small'
                            color='success'
                          />
                        ) : (
                          <Chip
                            icon={<HourglassEmptyIcon />}
                            label='Pending'
                            size='small'
                            color='warning'
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 170 }}>
                        {email.isPrimary ? (
                          <Tooltip title='Customers see this address as the From sender.'>
                            <Button
                              size='small'
                              variant='contained'
                              color='primary'
                              disableRipple
                              disableElevation
                              sx={{
                                width: 150,
                                cursor: 'default',
                                '&:hover': { boxShadow: 'none' }
                              }}
                            >
                              Sender
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={
                              !email.verified
                                ? 'Verify this email in SES before it can be the sender.'
                                : 'Make this the From address customers see.'
                            }
                          >
                            <span>
                              <Button
                                size='small'
                                variant='outlined'
                                sx={{ width: 150 }}
                                disabled={
                                  !email.verified || busyRowId === email.id
                                }
                                onClick={() => handleSetSender(email)}
                              >
                                Set as Sender
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(email.createdAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        {new Date(email.updatedAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        {!email.verified && (
                          <>
                            <Tooltip title='Resend verification email'>
                              <span>
                                <IconButton
                                  size='small'
                                  color='primary'
                                  onClick={() => handleResend(email)}
                                  disabled={busyRowId === email.id}
                                >
                                  <SendIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Check SES verification status'>
                              <span>
                                <IconButton
                                  size='small'
                                  onClick={() => handleRefreshStatus(email)}
                                  disabled={busyRowId === email.id}
                                >
                                  <RefreshIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={() => handleOpenDialog(email)}
                          title='Edit'
                        >
                          <EditIcon />
                        </IconButton>
                        <Tooltip
                          title={
                            !canDeleteEmail(email)
                              ? `Cannot delete the last email for ${
                                  email.type === 'ORDER_NOTIFICATION'
                                    ? 'Order Notification'
                                    : 'Customer Notification'
                                }. Each type must have at least one email.`
                              : 'Delete'
                          }
                        >
                          <span>
                            <IconButton
                              size='small'
                              color='error'
                              onClick={() => handleOpenDeleteDialog(email)}
                              disabled={!canDeleteEmail(email)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingEmail
              ? 'Edit Notification Email'
              : 'Add Notification Email'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {dialogError && (
                <Alert severity='error' onClose={() => setDialogError(null)}>
                  {dialogError}
                </Alert>
              )}
              <Alert severity='info'>
                {editingEmail
                  ? 'Email address cannot be changed. To use a different address, delete this entry and add a new one.'
                  : 'AWS SES will email a verification link to this address. It must be confirmed before notifications can use it.'}
              </Alert>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Notification Email Address'
                    type='email'
                    fullWidth
                    disabled={!!editingEmail}
                    error={!!errors.email}
                    helperText={
                      errors.email?.message ||
                      (editingEmail
                        ? 'Locked. Delete and re-add to use a different address.'
                        : 'This email will receive system notifications')
                    }
                    required
                  />
                )}
              />
              <Controller
                name='type'
                control={control}
                rules={{
                  required: 'Type is required'
                }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.type} required>
                    <InputLabel>Notification Type</InputLabel>
                    <Select {...field} label='Notification Type'>
                      <MenuItem value='ORDER_NOTIFICATION'>
                        Order Notification
                      </MenuItem>
                      <MenuItem value='CUSTOMER_NOTIFICATION'>
                        Customer Notification
                      </MenuItem>
                    </Select>
                    <FormHelperText>
                      {errors.type?.message ||
                        'Select the type of notification for this email'}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type='submit' variant='contained'>
              {editingEmail ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth='xs'
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center'
          }
        }}
      >
        <DialogTitle>Delete Notification Email</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the notification email{' '}
            <strong>{deletingEmail?.email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            variant='contained'
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default NotificationSettingsPage
